import React, { useMemo } from "react";

import Alert from "veneer/Alert";
import { ChartData } from "veneer/Chart";

import { KILOBYTE, MEGABYTE } from "cortex/ui/src/constant/app";

import ResourceLineChart from "./ResourceLineChart";
import { ChartPoint } from "../model";

interface NetworkChartProps {
  data: ReadonlyArray<ChartPoint>;
}

interface NetworkUsagePoint {
  x: number;
  y: number;
}

const DATA_SENT_LABEL = "Sent (KB)";
const DATA_RECEIVED_LABEL = "Received (MB)";
function getChartData(data: ReadonlyArray<ChartPoint>): ChartData<"line"> {
  const sentData: NetworkUsagePoint[] = [];
  const receivedData: NetworkUsagePoint[] = [];

  data.forEach(([timestamp, networkSend, networkReceive]) => {
    sentData.push({
      x: timestamp,
      y: networkSend / KILOBYTE,
    });
    receivedData.push({
      x: timestamp,
      y: networkReceive / MEGABYTE,
    });
  });

  return {
    datasets: [
      {
        borderWidth: 1,
        data: sentData,
        label: DATA_SENT_LABEL,
        pointRadius: 0,
      },
      {
        borderWidth: 1,
        data: receivedData,
        label: DATA_RECEIVED_LABEL,
        pointRadius: 0,
      },
    ],
  };
}

function formatTooltipValue(label: string, value: number): string {
  const unit = label === DATA_SENT_LABEL ? "KB" : "MB";
  return `${label}: ${value.toFixed(2)} ${unit}`;
}

export default function NetworkChart({ data }: NetworkChartProps) {
  const chartData = useMemo(() => getChartData(data), [data]);
  const timestamps = useMemo(
    () => data.map(([timestamp]) => timestamp),
    [data],
  );

  if (data.length === 0) {
    return (
      <Alert dense severity="info">
        Network data is not available for your device.
      </Alert>
    );
  }

  return (
    <ResourceLineChart
      data={chartData}
      formatTooltipValue={formatTooltipValue}
      timestamps={timestamps}
    />
  );
}
